import { formatDate } from "@angular/common";
import { Injectable } from "@angular/core";
import { AustralianState } from "@models/address/australian-state.enum";
import { Tag } from "@models/common/tag";
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { FilterOption } from "@models/grid/grid-filter";
import { Trust } from "@models/trusts/trust";
import { JurisdictionType } from "@models/trusts/trusts-enums";
import { AgDateCellComponent } from "@shared/components/grid/ag-cells/common/ag-date-cell/ag-date-cell.component";
import { AgEntityTagsComponent } from "@shared/components/grid/ag-cells/common/ag-entity-tags/ag-entity-tags.component";
import {
  AgTrustNameAbnComponent
} from "@shared/components/grid/ag-cells/trusts/ag-trust-name-abn/ag-trust-name-abn.component";
import {
  AgTrustStatusComponent
} from "@shared/components/grid/ag-cells/trusts/ag-trust-status/ag-trust-status.component";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { createFilterOptions } from "@shared/utils/create-filter-options.helper";
import { convertEnumToFilterOption } from "@shared/utils/enum-to-filter-option.helper";
import { applyTagGridFilterOption } from "@shared/utils/grid/apply-tag-grid-filter-option";
import { uniqueArrayToFilterOptionHelper } from "@shared/utils/unique-array-to-filter-option.helper";
import { mergeUniqueValues } from "@shared/utils/unique-merge.helper";
import { ColDef } from "ag-grid-community";
import { Subject } from "rxjs";

@Injectable()
export class TrustsGridService extends BaseGridService {
  refreshFiltersTrigger$ = new Subject<void>();

  // filters
  readonly companyStatusFilterOptions: FilterOption[] = [
    { label: 'Active', value: '0', active: true },
    { label: 'Archived', value: '1', active: false },
  ];

  trustTagFilterOptions: FilterOption[] = [];
  trustSystemTagFilterOptions: FilterOption[] = [];
  trustDateOfRegistrationFilterOptions: FilterOption[] = [];
  trustAccountManagerFilterOptions: FilterOption[] = [];
  trustPartnerManagerFilterOptions: FilterOption[] = [];
  familyGroupFilterOptions: FilterOption[] = [];
  trustJurisdictionOptions: FilterOption[] = [];

  refreshFilters(): void {
    this.refreshFiltersTrigger$.next();
  }

  setFiltersOptions(trusts: Trust[]): void {
    this.trustDateOfRegistrationFilterOptions = trusts.map(trust => {
      return {
        label: trust.dateOfEstablishment ? formatDate(trust.dateOfEstablishment ?? '', 'dd MMM yyyy', 'en') : '',
        value: trust.dateOfEstablishment ?? '',
        active: false
      };
    });

    const accountManagerList = trusts?.filter(t => t.accountManager?.fullName)
      .map(t => t.accountManager?.fullName) as string[];
    this.trustAccountManagerFilterOptions = (uniqueArrayToFilterOptionHelper([...new Set(accountManagerList)]));

    const partnerManagerList = trusts?.filter(t => t.partnerManager?.fullName)
      .map(t => t.partnerManager?.fullName) as string[];
    this.trustPartnerManagerFilterOptions = (uniqueArrayToFilterOptionHelper([...new Set(partnerManagerList)]));

    this.trustTagFilterOptions = createFilterOptions<Tag>(mergeUniqueValues<Tag[]>(trusts.map(c => c.tags), 'tagId'), 'name', 'name');
    this.trustSystemTagFilterOptions = createFilterOptions<Tag>(mergeUniqueValues<Tag[]>(trusts.map(c => c.systemTags), 'tagId'), 'name', 'name');

    const familyGroupList = trusts?.filter(c => c.familyGroup).map(c => c.familyGroup);
    this.familyGroupFilterOptions = (uniqueArrayToFilterOptionHelper([...new Set(familyGroupList)]));

    const jurisdictionOptions = convertEnumToFilterOption(AustralianState, true);
    jurisdictionOptions.forEach(o => o.value = (JurisdictionType[o.value as string] as number).toString());
    jurisdictionOptions.sort((a, b) => a.label.localeCompare(b.label));
    this.trustJurisdictionOptions = jurisdictionOptions;
  }

  override setColDefs(actionColumn: ColDef) {
    const colDefs: ColDef[] = [
      {
        headerName: 'Trust Name & ABN',
        exportColumnName: 'Trust Name',
        field: 'name',
        suppressMovable: true,
        width: 296,
        flex: 2,
        editable: false,
        sort: 'asc',
        cellRenderer: AgTrustNameAbnComponent,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: '',
        exportColumnName: 'ABN',
        field: 'abn',
        width: 100,
        flex: 0.5,
        menuTabs: [],
        hide: true
      },
      {
        headerName: 'Commencement Date',
        field: 'dateOfEstablishment',
        width: 80,
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.trustDateOfRegistrationFilterOptions,
        },
        getQuickFilterText: (params: { data: { abn: string } }) => {
          return params.data.abn ?? '';
        },
        cellDataType: CellDataType.DATE_STRING,
        cellRenderer: AgDateCellComponent,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 80,
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.companyStatusFilterOptions,
        },
        cellRenderer: AgTrustStatusComponent,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Tags',
        field: 'tags',
        width: 380,
        flex: 2,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.trustTagFilterOptions,
        },
        cellRendererParams: {
          allowEdit: true,
          handleAddNewTag: (() => this.refreshFilters()),
          onClickTag: this.onClickTag.bind(this)
        },
        cellRenderer: AgEntityTagsComponent,
        valueFormatter: (params: { value: Tag }) => params.value?.name ?? '',
        comparator: (tagListA: Tag[], tagListB: Tag[]) => {
          const a = tagListA?.length ?? 0;
          const b = tagListB?.length ?? 0;
          return a - b;
        },
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: '',
        exportColumnName: 'System Tags',
        field: 'systemTags',
        width: 180,
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.trustSystemTagFilterOptions,
        },
        valueFormatter: (params: { value: Tag }) => params.value?.name ?? '',
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Account Manager',
        field: 'accountManager.fullName',
        flex: 2,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.trustAccountManagerFilterOptions,
        },
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Partner',
        field: 'partnerManager.fullName',
        flex: 2,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.trustPartnerManagerFilterOptions,
        },
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Reference Number',
        field: 'referenceNumber',
        flex: 1,
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Family Group',
        field: 'familyGroup',
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.familyGroupFilterOptions,
        },
        cellDataType: CellDataType.TEXT,
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Jurisdiction',
        field: 'jurisdiction',
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.trustJurisdictionOptions,
        },
        valueFormatter: (params: { value: string }) => {
          return this.trustJurisdictionOptions.find(o => o.value == params.value)?.label ?? '';
        },
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      actionColumn
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));

    this.colDefs.set(colDefs);
  }

  onClickTag(tag: Tag): void {
    applyTagGridFilterOption(tag, this.colDefs, this.gridApi);
  }
}
